import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Le HTML d'un mail reçu vient de l'extérieur : il est rendu dans un cadre isolé, jamais dans la page.
// Sans allow-scripts, aucun script ni gestionnaire d'événement ne s'exécute ; sans allow-forms ni
// allow-top-navigation, le mail ne peut ni soumettre de formulaire ni quitter l'application. allow-same-origin
// (sans scripts dans le cadre) sert seulement à mesurer la hauteur du contenu. Ses styles restent dans le cadre.
export const MAIL_FRAME_SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const MAIL_FRAME_STYLE = `
body { margin: 0; font-family: Manrope, Arial, Helvetica, sans-serif; font-size: 14px; color: #122a26;
       line-height: 1.55; overflow-wrap: anywhere; word-break: normal; }
p, div { margin: 0 0 8px; }
p:last-child, div:last-child { margin-bottom: 0; }
ul, ol { margin: 6px 0 8px 18px; padding: 0; }
blockquote { margin: 8px 0; padding-left: 10px; border-left: 3px solid #cbd5e1; color: #64748b; }
a { color: #2563eb; text-decoration: underline; }
table { display: block; width: max-content; max-width: 100%; overflow-x: auto; border-collapse: collapse; background: white; }
th, td { padding: 4px 6px; border: 1px solid #cbd5e1; vertical-align: top; }
img { max-width: 100%; height: auto; }
`;

export function mailFrameDocument(html) {
    return "<!doctype html><html><head><meta charset=\"utf-8\">"
        + "<meta name=\"referrer\" content=\"no-referrer\"><base target=\"_blank\">"
        + `<style>${MAIL_FRAME_STYLE}</style></head><body>${html}</body></html>`;
}

function MailFrame({ html }) {
    const frameRef = useRef(null);
    const [height, setHeight] = useState(40);

    const measure = useCallback(() => {
        const doc = frameRef.current?.contentDocument;
        if (!doc?.documentElement) return;
        setHeight(Math.max(40, doc.documentElement.scrollHeight));
    }, []);

    useEffect(() => {
        const frame = frameRef.current;
        if (!frame || typeof ResizeObserver === "undefined") return undefined;
        let observer = null;
        const observe = () => {
            observer?.disconnect();
            const body = frame.contentDocument?.body;
            if (!body) return;
            observer = new ResizeObserver(measure);
            observer.observe(body);
        };
        frame.addEventListener("load", observe);
        return () => {
            frame.removeEventListener("load", observe);
            observer?.disconnect();
        };
    }, [measure]);

    return (
        <iframe
            ref={frameRef}
            className="mail-html-content"
            title="Contenu du mail"
            sandbox={MAIL_FRAME_SANDBOX}
            referrerPolicy="no-referrer"
            srcDoc={mailFrameDocument(html)}
            onLoad={measure}
            style={{ height }}
        />
    );
}

MailFrame.propTypes = {
    html: PropTypes.string.isRequired,
};

export default function MailMessageContent({ html, text }) {
    if (html) {
        return <MailFrame html={html} />;
    }

    return text || "(vide)";
}

MailMessageContent.propTypes = {
    html: PropTypes.string,
    text: PropTypes.string,
};
